import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AdminShell } from './AdminShell';
import { adminApi, AdminStats } from '@/lib/adminApi';

interface Tile {
  labelKey: string;
  value: number;
  format?: 'number' | 'cents';
}

function formatTile(t: Tile): string {
  if (t.format === 'cents') {
    return `¥${Math.round(t.value / 100).toLocaleString()}`;
  }
  return t.value.toLocaleString();
}

export function AdminStatsPage() {
  const { t } = useTranslation();
  const stats = useQuery<AdminStats>({
    queryKey: ['admin', 'stats'],
    queryFn: adminApi.stats,
  });

  const tiles: Tile[] = stats.data
    ? [
        { labelKey: 'admin.stats.users_total', value: stats.data.users_total },
        { labelKey: 'admin.stats.companies_total', value: stats.data.companies_total },
        { labelKey: 'admin.stats.workspaces_total', value: stats.data.workspaces_total },
        { labelKey: 'admin.stats.contacts_total', value: stats.data.contacts_total },
        { labelKey: 'admin.stats.accounts_total', value: stats.data.accounts_total },
        { labelKey: 'admin.stats.signals_last_24h', value: stats.data.signals_last_24h },
        { labelKey: 'admin.stats.deals_open', value: stats.data.deals_open },
        { labelKey: 'admin.stats.ai_runs_today', value: stats.data.ai_runs_today },
        {
          labelKey: 'admin.stats.ai_spend_today',
          value: stats.data.ai_spend_today_cents,
          format: 'cents',
        },
        { labelKey: 'admin.stats.audit_events_today', value: stats.data.audit_events_today },
      ]
    : [];

  return (
    <AdminShell titleKey="admin.stats.title">
      {stats.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}
      {stats.isError && (
        <p className="text-sm text-red-600">{t('admin.error_loading')}</p>
      )}
      {tiles.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {tiles.map((tile) => (
            <div
              key={tile.labelKey}
              className="rounded-2xl border border-ink-200 bg-white px-4 py-3"
            >
              <div className="text-[11px] uppercase tracking-wider text-ink-500">
                {t(tile.labelKey)}
              </div>
              <div className="mt-1 text-2xl font-semibold text-ink-900 num">
                {formatTile(tile)}
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminShell>
  );
}
